import { Redirect, Router } from "@reach/router";
import { GuestSignUpWelcome } from "components/GuestAuthForm/GuestSignUpWelcome";
import { ResetPassword } from "newui/features/Auth/components/ResetPassword";
import { MultiStep } from "components/SignUpForm/MultiStep";
import React, { useMemo } from "react";
import { Account } from "routes/Account";
import { Company } from "routes/Company";
import { Guest } from "routes/Guest";
import { NotAccessible } from "routes/NotAccessible";
import { NotFound } from "newui/features/ErrorPages/NotFound";
import { PrivacyPolicy } from "routes/PrivacyPolicy";
import ExternalRedirect from "routes/RedirectComp";
import { ShareProfile } from "routes/ShareProfile";
import { Supplier } from "routes/Supplier";
import { TermsConditions } from "routes/TermsConditions";
import { CONTACT_US_URL } from "utils/constants";

// import { LoginEmailOnly } from "newui/features/Auth/components/Login/OldLogin";
import Login from "newui/features/Auth/components/Login/Login";
import { Suspended } from "newui/features/Auth/components/Login/Suspended";
import { VerifyEmail } from "newui/features/Auth/components/Login/VerifyEmail";

import ClientViewRFxRedirect from "./ClientViewRFxRedirect";
import DeprecatedRfxSubmissionRedirect from "./DeprecatedRfxSubmissionRedirect";
import EmailVerification from "newui/features/Auth/components/Login/EmailVerification";
import { useCurrentProfileType } from "hooks/auth";
import { CompanyProfileType } from "newui/features/Auth/types/CompanyProfileType";
import { useAuthContext } from "newui/features/Auth/context/AuthContextProvider";
import { InvalidFileLinkError } from "newui/features/ErrorPages/InvalidFileLinkError";
import { selectProfileLoading } from "newui/features/Auth/authSelectors";
import { useSelector } from "react-redux";
import { PasswordResetForm } from "newui/features/Auth/components/PasswordResetForm";
import { RegistrationPage } from "newui/features/Auth/Registration/RegistrationPage";

interface AppRouterProps {
	isAccountSuspended?: boolean;
	isGuestLoggedIn?: boolean;
	isLoggedIn: boolean;
	isVerifiedMember?: boolean;
	userIsClient?: boolean;
	userIsSupplier?: boolean;
}

const invalidFileLink = <InvalidFileLinkError path="invalid-download" />;

const AppRouter: React.FC<AppRouterProps> = ({
	isLoggedIn,
	isGuestLoggedIn = false,
	isVerifiedMember = false,
	isAccountSuspended = true
}) => {
	const currentProfileType = useCurrentProfileType();
	const isProfileLoading = useSelector(selectProfileLoading);
	const { currentUserHomePage: homePage } = useAuthContext();

	if (isGuestLoggedIn) {
		return <Router style={{ display: "flex" }}>
			<Guest path="/account/*" />
			<Company path="/company/:companyId/*" />
			<ShareProfile path="/share-profile" />
			<ExternalRedirect
				path="/signup"
				to={CONTACT_US_URL}
			/>

			<Redirect
				from="/"
				to={homePage}
				noThrow
			/>
			{invalidFileLink}
			<NotFound default />
		</Router>
	}

	if (!isLoggedIn) {
		return <Router style={{ display: "flex" }}>
			<Login path="login/" />
			<Login path="member/login" />
			<Login path="/guest/login" />
			<Login
				path="/guest/login/:token"
			/>
			<GuestSignUpWelcome path="/thank-you" />
			<Login path="/" />
			<MultiStep path="signup-procuracon" />
			<ResetPassword path="reset-password" />
			<ResetPassword path="guest/reset-password" />
			<PasswordResetForm path="reset-password-form" />
			<PasswordResetForm path="guest/reset-password-form" />
			<RegistrationPage path="register" />
			<EmailVerification path="verification" />
			<PrivacyPolicy path="privacy-policy" />
			<TermsConditions path="terms-and-conditions" />
			<ExternalRedirect
				path="/signup"
				to={CONTACT_US_URL}
			/>
			<Redirect
				from="*"
				to="/login"
				noThrow
			/>
			{invalidFileLink}
		</Router>
	}

	if (isVerifiedMember && !isAccountSuspended) {
		return <Router style={{ display: "flex" }}>
			<Company path="company/*" />
			<Company path="company/:companyId/*" />
			<ClientViewRFxRedirect path="account/:companyId/request-for-quote/:rfxId/:currentTab/:slug" />
			<ClientViewRFxRedirect path="account/:companyId/request-for-quote/view-rfx/:rfxId/*" />
			<DeprecatedRfxSubmissionRedirect path="account/:companyId/request-for-quote/:rfxId/submission/:submissionCompanyId/:currentTab" />
			<DeprecatedRfxSubmissionRedirect
				isSupplier
				path="account/:companyId/request-for-quote/:rfxId/:currentTab"
			/>

			{currentProfileType === CompanyProfileType.SUPPLIER ? (
				<Supplier path="account/:companyId/*" />
			) : currentProfileType === CompanyProfileType.CLIENT ? (
				<Account path="account/:companyId/*" />
			) : null}

			<PrivacyPolicy path="privacy-policy" />
			<TermsConditions path="terms-and-conditions" />
			<ShareProfile path="/share-profile" />
			<NotAccessible path="/not-accessible" />
			<ExternalRedirect
				path="/signup"
				to={CONTACT_US_URL}
			/>
			<Redirect
				from="/"
				to={homePage}
				noThrow
			/>
			{invalidFileLink}
			{!isProfileLoading &&
				<NotFound default />}
		</Router>
	}

	return (
		<Router style={{ display: "flex" }}>
			{invalidFileLink}
			<NotFound default />
			<VerifyEmail path="verify-email" />
			<Suspended path="suspended" />
			<EmailVerification path="verification" />
			<PrivacyPolicy path="privacy-policy" />
			<TermsConditions path="terms-and-conditions" />
			<ExternalRedirect
				path="/signup"
				to={CONTACT_US_URL}
			/>
		</Router>
	);
};

export default AppRouter;
